import React, { useState, useEffect, useRef } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  deleteGameById,
  fetchGamesList,
  restoreGame,
  toggleGameStatus,
} from "../../services/axios/apiManager";
import { format } from "date-fns";
import Filters from "./filters";
import BSPagination from "../../Components/pagination";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";

const GamesList = ({
  games,
  genres,
  tagsData,
  setGames,
  languages,
  pagination,
  setPagination,
  granted,
}) => {
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [modalOptions, setModalOptions] = useState({});
  const navigate = useNavigate();
  const tableRef = useRef(null);

  // Fetch page of games
  const loadPage = async (page) => {
    const temp = { ...pagination, currentPage: page };
    setPagination(temp);
    const res = await fetchGamesList(temp.currentPage, temp.pageSize);
    if (res?.data?.data) setGames(res.data.data);
    if (res?.data?.pagination) setPagination(res.data.pagination);
  };

  // Page change handler
  const handlePageChange = (page) => {
    loadPage(page);
  };

  // Status toggle
  const handleStatusChange = (game) => {
    if (!granted?.some((g) => g.type === "toggle-status")) {
      return toast.error("No permission.", { duration: 3000 });
    }
    if (game?.deletedAt === null) {
      setModalOptions({
        header: "Change Status",
        body: `Toggle "${game.title}"?`,
        okay: "Change",
        method: changeStatus,
        data: { id: game.id, status: game.status === 1 ? 0 : 1 },
      });
      setShowOptionsModal(true);
    } else {
      toast.info("Cannot change status of a deleted record!", {
        duration: 2000,
      });
    }
  };
  const changeStatus = async (data) => {
    const res = await toggleGameStatus(data);
    if (res?.data?.success) {
      loadPage(pagination.currentPage);
      toast.info("Game status has been updated.");
    }
    setShowOptionsModal(false);
  };

  // Delete
  const handleDeleteGame = (game) => {
    if (!granted?.some((g) => g.type === "delete")) return;
    setModalOptions({
      header: "Delete Game",
      body: `Delete "${game.title}"?`,
      okay: "Delete",
      method: deleteGame,
      data: { id: game.id },
    });
    setShowOptionsModal(true);
  };
  const deleteGame = async (data) => {
    const res = await deleteGameById(data.id);
    if (res?.data?.success) {
      loadPage(pagination.currentPage);
      toast.info("Game data has been deleted.");
    }
    setShowOptionsModal(false);
  };

  // Restore
  const handleRestoreGame = (game) => {
    if (!granted?.some((g) => g.type === "delete")) return;
    setModalOptions({
      header: "Restore Game",
      body: `Restore "${game.title}"?`,
      okay: "Restore",
      method: restoreGameById,
      data: { id: game.id },
    });
    setShowOptionsModal(true);
  };
  const restoreGameById = async (data) => {
    const res = await restoreGame(data.id);
    if (res?.data?.success) {
      loadPage(pagination.currentPage);
      toast.info("Game data has been restored.");
    }
    setShowOptionsModal(false);
  };

  // Navigation helpers
  const viewGame = (id) => navigate(`/ViewGame/${id}`);
  const editGame = (id) => navigate(`/editGame/${id}`);
  const addNewGame = () => navigate("/addGame");

  // Initialize + re-init DataTable on games change
  useEffect(() => {
    if (!tableRef.current) return;
    const rows = games.map((g) => ({
      title: g.title,
      published_by: g.published_by,
      mob: g.mob_web_compatible ? "Yes" : "No",
      date: g.published_date ? format(g.published_date, "P") : "—",
      category: g.game_category?.name || "—",
      subcat: g.game_sub_category?.name || "—",
      orient: g.orientation,
      dims: g.dimensions,
      lang: g.language,
      status: `<button class="btn btn-sm status-btn ${
        g.status === 1 ? "text-success" : "text-danger"
      }" data-id="${g.id}" title="change status">${g.status === 1 ? "Active" : "Inactive"}</button>`,
      action: g.deletedAt
        ? [
            `<button class="btn btn-sm btn-outline-danger restore-btn" data-id="${g.id}" title="restore">
           <i class="fa-solid fa-rotate-right"></i>
         </button>`,
          ].join("")
        : [
            `<button class="btn btn-sm btn-outline-success view-btn" data-id="${g.id}" title="view details">
           <i class="fas fa-eye"></i>
         </button>`,
            granted?.some((p) => p.type === "update")
              ? `<button class="btn btn-sm btn-outline-primary edit-btn mx-1" data-id="${g.id}" title="edit">
               <i class="fas fa-edit"></i>
             </button>`
              : "",
            !g.is_associated &&
            granted?.some((p) => p.type === "delete") &&
            g.status === 0
              ? `<button class="btn btn-sm btn-outline-danger delete-btn" data-id="${g.id}" title="delete">
               <i class="fas fa-trash-alt"></i>
             </button>`
              : "",
          ].join(""),
    }));

    const config = {
      data: rows,
      columns: [
        { title: "Title", data: "title", className: "nowrap" },
        { title: "Publish By", data: "published_by", className: "nowrap" },
        { title: "Mobile Compatible", data: "mob", className: "nowrap" },
        { title: "Publish Date", data: "date", className: "nowrap" },
        { title: "Category", data: "category", className: "nowrap" },
        { title: "Sub_Category", data: "subcat", className: "nowrap" },
        { title: "Orientation", data: "orient", className: "nowrap" },
        { title: "Dimensions", data: "dims", className: "nowrap" },
        { title: "Language", data: "lang", className: "nowrap" },
        {
          title: "Status",
          data: "status",
          orderable: false,
          className: "text-center nowrap",
        },
        {
          title: "Actions",
          data: "action",
          orderable: false,
          className: "text-center nowrap",
        },
      ],

      destroy: true,
      paging: false,
      searching: false,
      info: false,
      responsive: true,
      autoWidth: false,
    };

    const $tbl = $(tableRef.current);
    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $tbl.DataTable().clear().destroy();
      $tbl.find("tbody").empty();
    }
    $tbl.DataTable(config);

    // Delegate clicks
    $tbl
      .off("click", ".view-btn")
      .on("click", ".view-btn", (e) => viewGame(+$(e.currentTarget).data("id")))
      .off("click", ".edit-btn")
      .on("click", ".edit-btn", (e) => editGame(+$(e.currentTarget).data("id")))
      .off("click", ".delete-btn")
      .on("click", ".delete-btn", (e) =>
        handleDeleteGame(
          games.find((g) => g.id === +$(e.currentTarget).data("id"))
        )
      )
      .off("click", ".status-btn")
      .on("click", ".status-btn", (e) =>
        handleStatusChange(
          games.find((g) => g.id === +$(e.currentTarget).data("id"))
        )
      )
      .off("click", ".restore-btn")
      .on("click", ".restore-btn", (e) =>
        handleRestoreGame(
          games.find((g) => g.id === +$(e.currentTarget).data("id"))
        )
      );

    return () => {
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $tbl.DataTable().destroy();
      }
    };
  }, [games, granted]);

  return (
    <div className="container py-4 text-light bg-dark rounded mb-4">
      <div className="row mb-2">
        <Filters
          pagination={pagination}
          setPagination={setPagination}
          genres={genres}
          tagsData={tagsData}
          languages={languages}
          setGames={setGames}
        />
        <div className="col-md-2 text-end">
          {granted?.some((g) => g.type === "create") && (
            <Button
              variant="success mt-2"
              onClick={addNewGame}
              title="Add new game"
            >
              Add New
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive">
        {/* DataTable */}
        <table
          ref={tableRef}
          className="table table-striped table-hover table-dark w-100"
        />
      </div>

      <div className="d-flex justify-content-end mt-2">
        <div className="mx-4 py-2">
            <span className="text-light">{`Rows/Page ${pagination.pageSize || 0}, Total records ${pagination?.totalRecords || 0}`}</span>
          </div>
        <BSPagination
          currentPage={Number(pagination.currentPage) || 1}
          totalPages={Number(pagination.totalPages) || Math.ceil((pagination.totalRecords || 0) / (pagination.pageSize || 10)) || 1}
          onPageChange={handlePageChange}
          siblingCount={2}
          showFirstLast={true}
        />
      </div>

      {/* Confirm Modal */}
      <Modal
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        centered
        className="text-light"
      >
        <Modal.Header closeButton className="bg-dark border-0">
          <Modal.Title>{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">{modalOptions.body}</Modal.Body>
        <Modal.Footer className="bg-dark border-0">
          <Button
            variant="secondary"
            onClick={() => setShowOptionsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => modalOptions.method(modalOptions.data)}
          >
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
        /* in a global CSS or SCSS file */
.nowrap {
  white-space: nowrap !important;
}
`}
      </style>
    </div>
  );
};

export default GamesList;
